import React, { useState } from 'react';
import { useWindowManager } from '../../components/Window/WindowManager';
import { UiButton } from '../../components/UiButton';
import WindowBody from '../../components/Window/WindowBody';
import WindowFooter from '../../components/Window/WindowFooter';

const SampleContent1: React.FC = () => {
  return (
    <>
      <WindowBody>
        <h4>Janela de Exemplo 1</h4>
        <p>Esta é uma janela de teste com conteúdo simples.</p>
        <p>Você pode:</p>
        <ul>
          <li>Arrastar pela barra de título</li>
          <li>Maximizar/Restaurar com o botão</li>
          <li>Clicar na janela para trazê-la para frente</li>
          <li>Fechar com o botão X</li>
        </ul>
      </WindowBody>
      <WindowFooter>
        <UiButton variant="secondary">Cancelar</UiButton>
        <UiButton variant="primary">Confirmar</UiButton>
      </WindowFooter>
    </>
  );
};

const SampleContent2: React.FC = () => {
  const [counter, setCounter] = useState(0);

  return (
    <>
      <WindowBody>
        <h4>Janela Interativa</h4>
        <p>Esta janela possui estado interno.</p>
        <div style={{ marginTop: '20px' }}>
          <p>Contador: <strong>{counter}</strong></p>
          <UiButton onClick={() => setCounter(counter + 1)}>Incrementar</UiButton>
        </div>
      </WindowBody>
      <WindowFooter>
        <UiButton variant="secondary" onClick={() => setCounter(0)}>Resetar</UiButton>
      </WindowFooter>
    </>
  );
};

const SampleContent3: React.FC = () => {
  const windowManager = useWindowManager();

  const openNestedWindow = () => {
    windowManager.openWindow({
      title: 'Janela Aninhada',
      component: SampleContent1,
      size: { width: 500, height: 400 }
    });
  };

  return (
    <>
      <WindowBody>
        <h4>Janela com Suporte a Aninhamento</h4>
        <p>Esta janela pode abrir outras janelas.</p>
        <p>Clique no botão abaixo para abrir uma janela aninhada:</p>
        <UiButton onClick={openNestedWindow} variant="success">
          Abrir Janela Aninhada
        </UiButton>
      </WindowBody>
      <WindowFooter>
        <UiButton variant="secondary">Fechar</UiButton>
      </WindowFooter>
    </>
  );
};

const LargeContent: React.FC = () => {
  return (
    <>
      <WindowBody>
        <h4>Janela com Scroll</h4>
        <p>Esta janela possui muito conteúdo para demonstrar o scroll vertical.</p>
        {Array.from({ length: 30 }).map((_, i) => (
          <p key={i}>
            Parágrafo {i + 1}: Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
          </p>
        ))}
      </WindowBody>
      <WindowFooter>
        <UiButton variant="primary">OK</UiButton>
      </WindowFooter>
    </>
  );
};

const MapContent: React.FC = () => {
  return (
    <WindowBody noPadding>
      <div style={{
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '24px',
        fontWeight: 'bold'
      }}>
        🗺️ Mapa Placeholder (sem padding)
      </div>
    </WindowBody>
  );
};

const WindowsSandbox: React.FC = () => {
  const windowManager = useWindowManager();

  const openWindow1 = () => {
    windowManager.openWindow({
      title: 'Janela Simples',
      component: SampleContent1,
      size: { width: 600, height: 400 }
    });
  };

  const openWindow2 = () => {
    windowManager.openWindow({
      title: 'Janela Interativa',
      component: SampleContent2,
      size: { width: 500, height: 350 }
    });
  };

  const openWindow3 = () => {
    windowManager.openWindow({
      title: 'Janela com Aninhamento',
      component: SampleContent3,
      size: { width: 700, height: 450 }
    });
  };

  const openLargeWindow = () => {
    windowManager.openWindow({
      title: 'Janela Grande com Scroll',
      component: LargeContent,
      size: { width: 800, height: 600 }
    });
  };

  const openSmallWindow = () => {
    windowManager.openWindow({
      title: 'Janela Pequena',
      component: () => (
        <WindowBody>
          <p>Janela pequena de teste</p>
        </WindowBody>
      ),
      size: { width: 300, height: 200 }
    });
  };

  const openMapWindow = () => {
    windowManager.openWindow({
      title: 'Visualizador de Mapa',
      component: MapContent,
      size: { width: 700, height: 500 }
    });
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>Sistema de Janelas - Sandbox</h2>
      <p>Teste as funcionalidades do sistema de janelas:</p>

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '20px' }}>
        <UiButton onClick={openWindow1} variant="primary">
          Abrir Janela Simples
        </UiButton>
        <UiButton onClick={openWindow2} variant="success">
          Abrir Janela Interativa
        </UiButton>
        <UiButton onClick={openWindow3} variant="info">
          Abrir Janela com Aninhamento
        </UiButton>
        <UiButton onClick={openLargeWindow} variant="warning">
          Abrir Janela Grande (Scroll)
        </UiButton>
        <UiButton onClick={openSmallWindow} variant="secondary">
          Abrir Janela Pequena
        </UiButton>
        <UiButton onClick={openMapWindow} variant="dark">
          🗺️ Abrir Mapa (sem padding)
        </UiButton>
      </div>

      <div style={{ marginTop: '40px', padding: '20px', background: '#f5f5f5', borderRadius: '8px' }}>
        <h4>Recursos Implementados:</h4>
        <ul>
          <li>✅ <strong>Centralização</strong>: Janelas sempre centralizadas no container (flex)</li>
          <li>✅ <strong>Limitação ao Container</strong>: Janela nunca ultrapassa o container, com respiro de 15px</li>
          <li>✅ <strong>Z-Index Dinâmico</strong>: Clique em uma janela para trazê-la para frente</li>
          <li>✅ <strong>Overlay Blocking</strong>: Apenas janela ativa é acessível, conteúdo abaixo bloqueado</li>
          <li>✅ <strong>Fechar</strong>: Botão X no header ou tecla ESC</li>
          <li>✅ <strong>Múltiplas Janelas</strong>: Abra quantas janelas quiser simultaneamente</li>
          <li>✅ <strong>Aninhamento</strong>: Janelas podem abrir outras janelas</li>
          <li>✅ <strong>Scroll</strong>: Conteúdo grande possui scroll automático</li>
          <li>✅ <strong>Padding Opcional</strong>: WindowBody com suporte a noPadding (ex: mapas)</li>
          <li>✅ <strong>Tamanhos Customizados</strong>: width/height configuráveis</li>
        </ul>
      </div>

      <div style={{ marginTop: '20px', padding: '20px', background: '#fff3cd', borderRadius: '8px' }}>
        <h4>Janelas Abertas:</h4>
        <p>Total: <strong>{windowManager.windows.length}</strong></p>
        {windowManager.windows.length === 0 && (
          <p style={{ color: '#666', fontStyle: 'italic' }}>Nenhuma janela aberta</p>
        )}
        {windowManager.windows.length > 0 && (
          <ul>
            {windowManager.windows.map(w => (
              <li key={w.id}>
                {w.title} - Z-Index: {w.zIndex}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default WindowsSandbox;
